/**
 * Bedienung der Ist-Zahlung an einer Ratenkarte (Plan 2026-10-04, Aufgabe 6): „Bezahlt markieren",
 * „Bezahlt am", „Zahlung zurücknehmen" (mit Bestätigung) und die Belege der Zahlung.
 *
 * Neu seit der Ist-Zahlung, kein Teil der Portierung aus `index.html`: dort gab es nur den Zahlstatus-Select.
 * Ein Wechsel nach `paid` oder zurück läuft nicht mehr über den Raten-PATCH (der Server antwortet 409),
 * sondern über diese Wege (`api/zahlungIst.ts`). Ohne `aktionen` (kein Recht `manage_payments`) bleibt
 * alles schreibgeschützt sichtbar. Einen Löschknopf je Beleg gibt es bewusst nicht.
 */
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Datumsfeld } from "../Datumsfeld.tsx";
import { Ikone } from "../../Ikone.tsx";
import { useDateiDownloadMitStapel } from "../../../api/useDateiDownload.ts";
import type { IstZahlung, Order, OrderDocument, OrderPayment } from "../../../lib/types.ts";

export interface IstZahlungAktionen {
  bezahltMarkieren: (rate: OrderPayment) => void;
  datumAendern: (zahlung: IstZahlung, paidOn: string) => void;
  /** Läuft erst NACH der Bestätigung im Dialog. */
  zuruecknehmen: (rate: OrderPayment, zahlung: IstZahlung) => void;
  belegHochladen: (zahlung: IstZahlung, datei: File) => void;
  belegVerknuepfen: (zahlung: IstZahlung, documentId: string) => void;
}

/** Dieselben Aktionen mit der Bestell-Id vorweg - so reicht `App.tsx` sie an `ZahlungTab` weiter. */
export type IstZahlungHandler = {
  [K in keyof IstZahlungAktionen]: (id: string, ...args: Parameters<IstZahlungAktionen[K]>) => void;
};

/** Auch die Liste „Gelöste Zahlungsbelege“ (`GeloesteBelege.tsx`) meldet einen gescheiterten Download mit diesem Wortlaut. */
export const BELEG_DOWNLOAD_FEHLGESCHLAGEN_MELDUNG = "Der Zahlungsbeleg konnte nicht heruntergeladen werden.";
const ISO_TAG = /^\d{4}-\d{2}-\d{2}$/;

/** Text des Bestätigungsdialogs - `n` Belege werden gelöst, nicht gelöscht. */
export function ruecknahmeText(n: number): string {
  return `Zahlung zurücknehmen? Die Rate wird wieder offen. ${n} Zahlungsbeleg(e) werden von dieser Zahlung gelöst, aber nicht gelöscht. Sie bleiben gespeichert und können erneut verknüpft werden.`;
}

function RuecknahmeDialog({
  n,
  onAbbrechen,
  onBestaetigen,
}: {
  n: number;
  onAbbrechen: () => void;
  onBestaetigen: () => void;
}) {
  return createPortal(
    <div
      className="board-editor-overlay open"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onAbbrechen();
      }}
    >
      <section className="board-editor-dialog" role="alertdialog" aria-modal="true" aria-labelledby="ruecknahmeTitel">
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="ruecknahmeTitel">Zahlung zurücknehmen</h2>
            <p>{ruecknahmeText(n)}</p>
          </div>
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onAbbrechen}>
            Abbrechen
          </button>
          <button className="btn-primary" type="button" onClick={onBestaetigen}>
            Zurücknehmen
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}

/** Die `documentId`s aller Belege, die an irgendeiner Ist-Zahlung der Bestellung hängen. Auch `GeloesteBelege.tsx` blendet sie aus. */
export function verknuepfteDokumentIds(o: Order): Set<string> {
  const ids = new Set<string>();
  for (const p of (Array.isArray(o.payments) ? o.payments : []) as OrderPayment[]) {
    for (const d of p.actual?.documents ?? []) ids.add(d.documentId);
  }
  return ids;
}

/** Dokumente der Bestellung, die noch an keiner Ist-Zahlung hängen (je `documentId` einmal), dazu die gelösten Zahlungsbelege (`o.geloesteZahlungsbelege`). */
export function verknuepfbareDokumente(o: Order): OrderDocument[] {
  const verknuepft = verknuepfteDokumentIds(o);
  const gesehen = new Set<string>();
  const ergebnis: OrderDocument[] = [];
  for (const d of (o.pdfs as OrderDocument[] | undefined) ?? []) {
    const id = typeof d.documentId === "string" ? d.documentId : "";
    if (id === "" || verknuepft.has(id) || gesehen.has(id)) continue;
    gesehen.add(id);
    ergebnis.push(d);
  }
  for (const g of o.geloesteZahlungsbelege ?? []) {
    if (g.documentId === "" || verknuepft.has(g.documentId) || gesehen.has(g.documentId)) continue;
    gesehen.add(g.documentId);
    ergebnis.push({ documentId: g.documentId, name: g.name, path: g.path, type: g.docType });
  }
  return ergebnis;
}

/** Knopf „Bezahlt markieren" einer offenen/fixierten Rate. */
export function BezahltMarkieren({
  p,
  aktionen,
  gesperrt,
}: {
  p: OrderPayment;
  aktionen: IstZahlungAktionen;
  gesperrt?: boolean;
}) {
  return (
    <button
      type="button"
      className="btn-ghost"
      data-permission="manage_payments"
      disabled={gesperrt}
      onClick={() => aktionen.bezahltMarkieren(p)}
    >
      <Ikone name="circle-check-big" />
      Bezahlt markieren
    </button>
  );
}

/** Datum, Rücknahme und Belege einer Rate MIT Ist-Zahlung. */
export function IstZahlungBlock({
  o,
  p,
  zahlung,
  aktionen,
  gesperrt,
}: {
  o: Order;
  p: OrderPayment;
  zahlung: IstZahlung;
  aktionen?: IstZahlungAktionen;
  gesperrt?: boolean;
}) {
  const [dialog, setDialog] = useState(false);
  const [auswahl, setAuswahl] = useState("");
  const dateiFeld = useRef<HTMLInputElement>(null);
  // Fehleranzeige Paket 1, Task 8 (Spec R2): die Zeile unter den Belegen entfaellt, ein gescheiterter Download
  // geht an den Stapel - wie der Download im uebrigen Zahlungs-Reiter.
  const { herunterladen } = useDateiDownloadMitStapel(BELEG_DOWNLOAD_FEHLGESCHLAGEN_MELDUNG);
  const verknuepfbar = aktionen ? verknuepfbareDokumente(o) : [];

  return (
    <div className="payment-status-field payment-ist" data-testid="ist-zahlung">
      <label>
        Bezahlt am
        <Datumsfeld
          wert={zahlung.paidOn}
          disabled={!aktionen || gesperrt}
          onCommit={(wert) => {
            if (aktionen && ISO_TAG.test(wert)) aktionen.datumAendern(zahlung, wert);
          }}
        />
      </label>
      {aktionen ? (
        <button
          type="button"
          className="btn-ghost"
          data-permission="manage_payments"
          disabled={gesperrt}
          onClick={() => setDialog(true)}
        >
          Zahlung zurücknehmen
        </button>
      ) : null}
      <div className="doc-list">
        {zahlung.documents.length ? (
          zahlung.documents.map((d) => (
            <div className="doc-row" key={d.documentId}>
              <div className="doc-ic">{(d.name.split(".").pop() || "DOC").toUpperCase().slice(0, 4)}</div>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  void herunterladen(d.path, d.name);
                }}
              >
                {d.name}
              </a>
            </div>
          ))
        ) : (
          <div className="payment-receipt-empty">Noch kein Beleg an dieser Zahlung.</div>
        )}
      </div>
      {aktionen ? (
        <div className="payment-ist-belege" data-permission="manage_payments">
          <input
            ref={dateiFeld}
            type="file"
            hidden
            aria-label="Beleg Datei"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.ods,.csv,.png,.jpg,.jpeg"
            onChange={(e) => {
              const datei = e.currentTarget.files?.[0];
              e.currentTarget.value = "";
              if (datei) aktionen.belegHochladen(zahlung, datei);
            }}
          />
          <button type="button" className="btn-ghost" disabled={gesperrt} onClick={() => dateiFeld.current?.click()}>
            <Ikone name="upload" />
            Beleg hochladen
          </button>
          {verknuepfbar.length ? (
            <select
              aria-label="Vorhandenes Dokument verknüpfen"
              value={auswahl}
              disabled={gesperrt}
              onChange={(e) => {
                const id = e.currentTarget.value;
                setAuswahl("");
                if (id !== "") aktionen.belegVerknuepfen(zahlung, id);
              }}
            >
              <option value="">Vorhandenes Dokument verknüpfen …</option>
              {verknuepfbar.map((d) => (
                <option key={d.documentId as string} value={d.documentId as string}>
                  {d.name as string}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      ) : null}
      {dialog && aktionen ? (
        <RuecknahmeDialog
          n={zahlung.documents.length}
          onAbbrechen={() => setDialog(false)}
          onBestaetigen={() => {
            setDialog(false);
            aktionen.zuruecknehmen(p, zahlung);
          }}
        />
      ) : null}
    </div>
  );
}
